<template>
  <div
    id="view-panel-wrapper"
  >
    <header
      class="px-5 py-4 m-0 bg-white d-flex border-bottom"
    >
      <b-container fluid>
        <b-row>
          <b-col
            class="d-flex flex-row align-items-center position-relative"
            lg="9"
          >
            <h5
              class="m-1 text-wrap template-name"
              data-test="creative-name"
            >
              {{ getSelectedCreativeTemplateName }}
            </h5>
            <PlayPauseButton
              v-if="displayCreativeContentStateButton"
              :content-item="contentItem"
              :is-content-item-published="isContentItemPublished"
              :selected-creative-content="getSelectedCreativeContent"
              :is-creative-content-active="isCreativeContentActive"
            />
          </b-col>
          <b-col
            self-align="right"
            v-if="isManagedCampaign"
          >
            <b-button
              data-test="manage-trackers"
              size="sm"
              @click="showManageTrackers"
            >
              Manage Trackers
            </b-button>
          </b-col>
        </b-row>
      </b-container>
    </header>
    <div
      ref="viewPoint"
      class="view-point d-flex flex-grow"
    >
      <CreativeCarousel
        :perspective="0"
        :controls-visible="true"
        :disable3d="true"
        :space="viewWidth"
        :display="activeCreativesCount"
        :width="viewWidth"
        :height="viewHeight-40"
        :one-directional="true"
        @goNext="updateDisplayedCreative(true)"
        @goPrevious="updateDisplayedCreative(false)"
      >
        <CarouselSlide
          v-for="(creative, key) in activeCreatives"
          :key="key"
          :index="key"
          class="d-flex"
        >
          <RenderedTemplate
            :ref="`creativeRender-${creative.id}`"
            class="creative-slide"
            :read-only="readOnly"
            :editable-properties="templateEditableProperties"
            :creative-set="creative"
            :content-item="contentItem"
            :current-creative-id="currentCreativeId"
            :view-size="{ viewWidth:(viewWidth-15), viewHeight:(viewHeight-40)}"
            :is-creative-content-active="isCreativeContentActive"
            :is-content-item-paused="isContentItemPaused"
          />
        </CarouselSlide>
      </CreativeCarousel>
    </div>

    <PreviewRightPanel
      :read-only="readOnly"
      :editable-properties="templateEditableProperties"
      :view-type="viewType"
      :content-item="contentItem"
      @iframeLoading="iframeLoading()"
      @iframeLoad="rerenderCreative({ getDisplayCopy: false })"
    />
    <BottomPanel
      ref="editPanel"
      v-if="showEditPanel"
      :content-item="getCurrentSelectedCreativeDisplayCopy"
      :feed-id="feedId"
      :advertisers-id="getAdvertiserId"
      :read-only="readOnly"
      :view-type="viewType"
      :creative-set="creativeSet"
      @contentLoad="rerenderCreative()"
      @refreshContentList="refreshContentList"
    />
  </div>
</template>

<script>
import PreviewRightPanel from '@/modules/content/components/PreviewRightPanel/Sidebar';
import BottomPanel from '@/modules/content/components/BottomPanel';
import RenderedTemplate from '@/modules/templates/components/RenderedTemplate';
import CreativeCarousel from '@/modules/templates/components/CreativeCarousel';
import CarouselSlide from '@/modules/templates/components/CarouselSlide';
import PlayPauseButton from '@/modules/content/components/PlayPauseButton.vue';

import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import * as types from '@/modules/content/store/types';
import * as templatesTypes from '@/modules/templates/store/types';
import * as trackerTypes from '@/modules/trackers/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';

export default {
  name: 'MultipleCreativeContentPreview',
  components: {
    PreviewRightPanel,
    BottomPanel,
    CreativeCarousel,
    CarouselSlide,
    RenderedTemplate,
    PlayPauseButton,
  },
  data() {
    return {
      iframeLoaded: false,
      // timestamp: new Date().getTime(),
      availableTemplateGroups: [],
      availableTemplates: [],
      viewWidth: 700,
      viewHeight: 600,
    };
  },
  props: {
    creativeSet: {
      type: Object,
      default: () => {},
    },
    contentItem: {
      type: Object,
      default: () => {},
    },
    readOnly: {
      type: Array,
      default: () => [],
    },
    viewType: {
      type: String,
      default: 'Editor',
    },
    selectedItem: {
      type: String,
      default: 'Editor',
    },
  },
  computed: {
    ...mapState('campaigns', [
      'selectedAdvertiser',
      'campaign',
    ]),
    ...mapState('content', [
      'allowAnnotations',
      'currentAnnotation',
      'currentAnnotationState',
      'feedId',
      'activeCreatives',
      'selectedCreativeId',
      'creative',
      'creativeContentStates',
    ]),
    ...mapGetters('content', {
      getDisplayCopy: types.GET_CURRENT_SELECTED_CREATIVE_DISPLAY_COPY,
      getSelectedCreative: types.GET_CURRENT_SELECTED_CREATIVE,
    }),
    ...mapGetters('templates', {
      templateEditableProperties: templatesTypes.GET_TEMPLATE_EDITABLE_PROPERTIES,
    }),
    ...mapGetters('campaigns', {
      isManagedCampaign: campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      isCampaignLive: campaignTypes.GET_IS_CAMPAIGN_LIVE,
    }),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_IS_NEW_CAMPAIGN,
      campaignTypes.GET_IS_CAMPAIGN_DRAFT,
    ]),
    currentCreativeId() {
      return this.getSelectedCreative.id;
    },
    getSelectedCreativeContent() {
      const { encodedExternalId } = this.contentItem;
      const selectedCreative = this.creativeContentStates?.filter(
        (contentCreative) => contentCreative.contentItemUuid === encodedExternalId);
      return selectedCreative[0];
    },
    isContentItemPaused() {
      return (this.contentItem.state === 'PAUSED');
    },
    isContentItemPublished() {
      return (this.contentItem.state === 'PUBLISHED');
    },
    isCreativeContentActive() {
      return this.getSelectedCreativeContent?.active;
    },
    displayCreativeContentStateButton() {
      return this.isContentItemPublished && this.isCampaignLive && this.isManagedCampaign;
    },
    showEditPanel() {
      return this.selectedItem !== '';
    },
    getAdvertiserId() {
      if (this.selectedAdvertiser !== {}) {
        return this.selectedAdvertiser.id;
      }
      return 0;
    },
    getCurrentSelectedCreativeDisplayCopy() {
      const { encodedExternalId, id } = this.contentItem;
      return {
        ...this.getDisplayCopy(encodedExternalId),
        encodedExternalId,
        id,
      };
    },
    activeCreativesCount() {
      return this.activeCreatives.length;
    },
    getSelectedCreativeTemplateName() {
      if (this.availableTemplates.length === 0) return '';
      const template = this.availableTemplates
        .find((item) => item.id === this.getSelectedCreative.creativeTemplateId);
      return template ? template.name : '';
    },
  },
  methods: {
    ...mapActions('content', [
      // types.GET_TEMPLATE_BY_ID,
      types.SET_CURRENT_ANNOTATION,
      types.REMOVE_CURRENT_ANNOTATION,
      types.FETCH_CONTENT_DISPLAY_COPY,
      types.UPDATE_CURRENT_SELECTED_CREATIVE_ID,
      types.FETCH_EXISTING_CREATIVES,
      types.FETCH_CREATIVE_CONTENT_STATES,
      types.FETCH_CONTENT_COMMENTS_COUNT,
      types.FETCH_CREATIVE_TEMPLATES,
    ]),
    ...mapActions('trackers', [
      trackerTypes.FETCH_SELF_SERVICE_CONTENT_TRACKERS_MULTIPLE_CREATIVE,
    ]),
    ...mapActions('templates', [
      templatesTypes.FETCH_TEMPLATES_IN_GROUP_ID,
      templatesTypes.FETCH_TEMPLATE_GROUPS,
    ]),
    ...mapMutations('content', [
      types.CLEAR_CURRENT_COMMENTS,
      types.SET_SELECTED_CREATIVE_ID,
    ]),
    iframeLoading() {
      this.iframeLoaded = false;
    },
    isReadOnly(property) {
      return this.readOnly.includes(property);
    },
    isEditableProperty(property) {
      return this.templateEditableProperties.includes(property);
    },
    async updateDisplayedCreative(next = true) {
      await this.UPDATE_CURRENT_SELECTED_CREATIVE_ID(next);
      this.FETCH_CONTENT_COMMENTS_COUNT(this.selectedCreativeId);
      this.fetchCreativeContentStates(this.selectedCreativeId);
    },
    async getContentDisplayCopy() {
      await this.FETCH_CONTENT_DISPLAY_COPY(!this.isDraft());
    },
    isDraft() {
      return this.viewType === 'Setup';
    },
    rerenderCreative({ getDisplayCopy = true } = {}) {
      const { id } = this.getSelectedCreative;
      if (getDisplayCopy) this.getContentDisplayCopy();
      this.$refs[`creativeRender-${id}`][0].rerenderCreative();
    },
    fetchContentTrackers() {
      const { advertiserId } = this.$route.params;
      this.FETCH_SELF_SERVICE_CONTENT_TRACKERS_MULTIPLE_CREATIVE({ advertiserId, creativeId: this.currentCreativeId });
    },
    fetchTemplateGroups() {
      this.FETCH_TEMPLATE_GROUPS()
        .then((groups) => {
          this.availableTemplateGroups = groups;
          this.getCreativeTemplates();
        });
    },
    getTemplateGroupId(groups) {
      return !this.GET_IS_MANAGED_CAMPAIGN ? groups.find(((group) => group.name === 'Self Service'))?.id : this.campaign.templateGroupId;
    },
    getCreativeTemplates() {
      if (this.GET_IS_MANAGED_CAMPAIGN && this.GET_IS_NEW_CAMPAIGN) return;
      const payload = {
        templateGroupId: this.getTemplateGroupId(this.availableTemplateGroups),
        campaignId: this.campaign.id,
      };
      if (this.GET_IS_MANAGED_CAMPAIGN || this.GET_IS_NEW_CAMPAIGN) delete payload.campaignId;
      else if (!this.GET_IS_CAMPAIGN_DRAFT) delete payload.templateGroupId;
      this.FETCH_CREATIVE_TEMPLATES(payload).then((templates) => { this.populateCreativeTemplateList(templates); });
    },
    populateCreativeTemplateList(templates) {
      this.availableTemplates = templates;
    },
    fetchSelfServiceTemplates() {
      this.getCreativeTemplates();
    },
    fetchTemplatesById() {
      this.getCreativeTemplates();
    },
    refreshContentList() {
      this.$nextTick(() => {
        this.$emit('refreshContentList');
      });
    },
    showManageTrackers() {
      this.$emit('showManageTrackers');
    },
    async fetchCreativeContentStates(activeCreativeId) {
      if (!this.isManagedCampaign || !this.isCampaignLive) return;
      await this.FETCH_CREATIVE_CONTENT_STATES({ advertiserId: this.selectedAdvertiser.id, creativeId: activeCreativeId });
    },
  },
  mounted() {
    this.getContentDisplayCopy();

    if (this.campaign.templateGroupId == null) {
      this.fetchSelfServiceTemplates();
    } else {
      this.fetchTemplatesById(this.campaign.templateGroupId);
    }
    this.$nextTick(() => {
      // this.viewWidth = this.$refs.viewPoint.clientWidth;
      // this.viewHeight = this.$refs.viewPoint.clientHeight;
    });
    if (this.activeCreatives.length > 0) {
      this.SET_SELECTED_CREATIVE_ID(this.activeCreatives[0].id);
    }
    this.fetchCreativeContentStates(this.selectedCreativeId);
  },
  watch: {
    contentItem(newval) {
      if (!newval) this.iframeLoaded = false;
      else this.iframeLoaded = true;
      this.getContentDisplayCopy();
    },
    getSelectedCreative() {
      if (!this.isManagedCampaign) {
        this.fetchContentTrackers();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.template-name{
  width: 40%;
}
  .flex-grow {
    flex: 1 0 auto;
  }
  #view-panel-wrapper{
    height: calc(100% - 56px);
  }
  .view-point::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  .view-point::-webkit-scrollbar-thumb {
    background: #666;
    border-radius: 20px;
  }

  .view-point::-webkit-scrollbar-track {
    background: #ddd;
    border-radius: 20px;
  }
  .view-point {
    margin-right: 58px;
    height: inherit;
    overflow: auto;
  }
  .creative-wrapper {
    position: relative;
    margin-bottom: 14px;
    background: rgba(0, 0, 0, 0.2);
    box-shadow: -10px 10px 10px rgba(0, 0, 0, 0.3);
    -moz-box-shadow: -10px 10px 10px rgba(0, 0, 0, 0.3);
    -webkit-box-shadow: -10px 10px 10px rgba(0, 0, 0, 0.3);
  }

.tab-content {
  height: 100%;
  overflow: hidden;
}
.tab-content-fill {
  height: calc(100% - 65px);
  padding-bottom: 69px;
}
.creative-slide {
  height: 100%;
  display: flex;
  justify-content: center;
}
</style>
